import { getLastCommentVisibility, rememberCommentVisibility } from '@overlayqa/shared';
import type { CommentAttachmentUpload } from '@overlayqa/shared';
import { useCallback, useRef, useState } from 'react';
import { newUuid, type CommentVisibility, type MentionableUser } from '@overlayqa/shared';
import { toast } from 'sonner';
import { useCreateComment } from '../../hooks/useIssueComments';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';
import { contentForStorage, extractMentionIds } from './commentMentions';

/** A draft's audience and request identity travel together through send/retry.
 * A successful acknowledgement clears only the submitted text revision. */
export function useCommentDraft(issueId: string, teamMembers: MentionableUser[]) {
  const createComment = useCreateComment(issueId);
  const [content, setContentState] = useState('');
  const [saved, setSaved] = useState<{ at: number; visibility: CommentVisibility } | null>(null);
  const [attachments, setAttachments] = useState<CommentAttachmentUpload[]>([]);
  const [readingFiles, setReadingFiles] = useState(false);
  const [visibility, setVisibilityState] = useState<CommentVisibility>(getLastCommentVisibility);
  const setVisibility = useCallback((value: CommentVisibility) => { setVisibilityState(value); rememberCommentVisibility(value); }, []);
  const revision = useRef(0);
  const creating = useRef(false);
  const attempt = useRef<{ body: string; key: string } | null>(null);
  const setContent = useCallback((value: string) => {
    revision.current += 1;
    setContentState(value);
  }, []);

  const submit = useCallback(async () => {
    const trimmed = content.trim();
    if ((!trimmed && !attachments.length) || readingFiles || creating.current || createComment.isPending) return;
    creating.current = true;
    const submittedRevision = revision.current;
    const resolved = contentForStorage(trimmed, teamMembers);
    const mentionedUserIds = extractMentionIds(resolved);
    capture('comment_posted', { has_mentions: mentionedUserIds.length > 0, visibility, surface: 'dashboard' });
    const body = JSON.stringify({ issueId, content: resolved, visibility, attachments });
    if (attempt.current?.body !== body) attempt.current = { body, key: newUuid() };
    try {
      await createComment.mutateAsync({
        content: resolved,
        mentionedUserIds,
        visibility,
        ...(attachments.length ? { attachments } : {}),
        idempotencyKey: attempt.current.key,
      });
      setSaved({ at: Date.now(), visibility });
      attempt.current = null;
      setAttachments(current => current.filter(file => !attachments.some(sent => sent.id === file.id)));
      if (revision.current === submittedRevision) {
        setContent('');
      }
    } catch (err) {
      log.error('[CommentThread] failed to create comment:', { err });
      toast.error(err instanceof Error && err.message ? err.message : 'Failed to post comment.');
    } finally {
      creating.current = false;
    }
  }, [issueId, content, visibility, attachments, readingFiles, createComment, teamMembers, setContent]);

  return { saved, content, setContent, visibility, setVisibility, attachments, setAttachments, readingFiles, setReadingFiles, submit, isPending: createComment.isPending };
}
